<template>
  <div>
    <div class="flex flex-col">
      <div class="w-full bg-action-bar px-2 py-2 clearfix">
        <!-- Dutch customers (country_origin 'nl') handled in the Swedish portal -->
        <div class="float-left flex flex-wrap" v-if="canFilterDutch">
          <button :class="[dutchOnly ? 'btn-blue' : 'btn-blue-outline', 'mx-2 mt-2 lg:mt-0']" @click.prevent="toggleDutch()">
            {{ $t("country_origin.dutch_customers") }}
          </button>
        </div>
        <div class="float-right flex flex-wrap">
          <div class="mx-2 mt-2 lg:mt-0">
            <button class="btn-blue-outline mx-2 mt-3 sm:mt-0" @click.prevent="exportXLS()">
              {{ $t("export") }}
              <BaseIcon icon="file-excel" class="ml-1" />
            </button>
            <router-link class="btn-green" to="/admin/skapa-kund">
              {{ $t("new_customer") }}
              <BaseIcon icon="plus" class="ml-2" />
            </router-link>
          </div>
        </div>
      </div>
      <div class="w-full mt-6">
        <vue-good-table
          ref="customersTable"
          mode="remote"
          styleClass="vgt-table"
          row-style-class="text-sm"
          :columns="columns"
          :rows="customers"
          :totalRows="totalRecords"
          :search-options="{
            enabled: true,
            placeholder: 'Sök',
          }"
          :pagination-options="{
            enabled: true,
            perPage: 100,
            perPageDropdown: [100],
            dropdownAllowAll: true,
          }"
          @on-page-change="onPageChange"
          @on-per-page-change="onPerPageChange"
          @on-sort-change="onSortChange"
          @on-search="onSearch"
        >
          <template slot="table-row" slot-scope="props">
            <span v-if="props.column.field == 'name'">
              <router-link v-if="user.role == 'admin'" class="text-primary-500 font-bold underline hover:text-primary-600 transition duration-200" :to="'/startsida/' + props.row.customer_id">
                {{ props.row.name }}
              </router-link>
              <span v-else>{{ props.row.name }}</span>
            </span>
            <span v-else-if="props.column.field == 'agreement' && user.role == 'admin'">
              <router-link tag="button" class="btn-blue-outline inline-flex" :to="'/mina-avtal/customer/' + props.row.customer_id">
                {{ $t("agreement") }}
                <BaseIcon icon="file" class="ml-1" />
              </router-link>
            </span>
            <span v-else-if="props.column.field == 'tounit'">
              <router-link tag="button" class="btn-blue-outline inline-flex" :to="'/verksamheter/customer/' + props.row.customer_id + '/-'">
                {{ $t("operation") }}
                <BaseIcon icon="city" class="ml-1" />
              </router-link>
            </span>
            <span v-else-if="props.column.field == 'toaddon'">
              <button v-if="user.role == 'admin' || user.role == 'seller'" class="btn-blue-outline" @click.prevent="showAddon(props.row)">
                <BaseIcon icon="cube" />
              </button>
            </span>
            <span v-else-if="props.column.field == 'edit'">
              <button class="btn-secondary-outline" @click.prevent="show(props.row)">
                <BaseIcon icon="pencil-alt" />
              </button>
            </span>
            <span v-else-if="props.column.field == 'delete'">
              <button v-if="user.role == 'admin'" class="btn-red-outline" @click.prevent="deleteCustomerPrompt(props.row)">
                <BaseIcon icon="trash" />
              </button>
            </span>
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>
      </div>
    </div>
    <CustomerUpdate :customerInfo="customer_info" :alarmCenters="alarm_centers" :sellers="sellers" @updateCustomer="loadCustomers()" />
    <AddonsUpdate :customerInfo="customer_info" />
  </div>
</template>

<script>
import CustomerUpdate from "@/components/modal/customer_update";
import AddonsUpdate from "@/components/modal/addons_update";
import { canFilterDutch, DUTCH_ORIGIN } from "@/utils/countryOrigin";

export default {
  title() {
    return this.$t("page_titles.customer");
  },
  components: {
    CustomerUpdate,
    AddonsUpdate,
  },
  data() {
    return {
      user: this.$store.state.user,
      customers: [],
      alarm_centers: [],
      sellers: [],
      customer_info: null,
      totalRecords: 0,
      serverParams: {
        page: 1,
        perPage: 100,
        search: '',
        sortField: '_id',
        sortType: 'asc',
      },
      searchTimer: null,
      dutchOnly: false, // "Nederländska kunder"
    };
  },

  methods: {
    updateParams(newProps) {
      this.serverParams = Object.assign({}, this.serverParams, newProps);
    },
    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadCustomers();
    },
    onPerPageChange(params) {
      this.updateParams({ perPage: params.currentPerPage, page: 1 });
      this.loadCustomers();
    },
    onSortChange(params) {
      if (!params[0]) return;
      this.updateParams({ sortField: params[0].field, sortType: params[0].type, page: 1 });
      this.loadCustomers();
    },
    onSearch(params) {
      sessionStorage.setItem('customers_search', params.searchTerm);
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.updateParams({ search: params.searchTerm, page: 1 });
        this.loadCustomers();
      }, 400);
    },
    toggleDutch() {
      this.dutchOnly = !this.dutchOnly;
      sessionStorage.setItem('customers_dutch', this.dutchOnly ? '1' : '');
      this.updateParams({ page: 1 });
      this.loadCustomers();
    },
    // the list filters that also apply to the export
    filterParams() {
      return this.dutchOnly ? { countryOrigin: DUTCH_ORIGIN } : {};
    },
    loadCustomers() {
      const params = {
        page: this.serverParams.page,
        perPage: this.serverParams.perPage,
        search: this.serverParams.search,
        sortField: this.serverParams.sortField,
        sortType: this.serverParams.sortType,
        ...this.filterParams(),
      };
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/customer/`, { params })
        .then((response) => {
          this.customers = response.data.data;
          this.totalRecords = response.data.totalRecords;
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    getSellers() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/seller/reseller`)
        .then((response) => {
          this.sellers = response.data.data;
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    getAlarmCentrals() {
      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/alarm-central/`)
        .then((response) => {
          this.alarm_centers = response.data.data;
        })
        .catch((error) => {
          this.handleError(error);
        });
    },

    deleteCustomerPrompt(customer) {
      this.$modal.show("dialog", {
        title: `<span class="text-red-500">${this.$t('delete_customer_modal.title', { name: customer.name })}</span><br>
                <span class="font-normal text-sm text-secondary-600">${this.$t('delete_customer_modal.subtitle')}</span>`,
        buttons: [
          {
            title: `<div class="bg-accent-500 text-white text-sm font-sans py-2">${this.$t('delete_customer_modal.cancel')}</div>`,
            handler: () => {
              this.$modal.hide("dialog");
            },
          },
          {
            title: `<div class="bg-red-500 text-white text-sm font-sans py-2">${this.$t('delete_customer_modal.delete')}</div>`,
            handler: () => {
              this.deleteCustomer(customer._id);
            },
          },
        ],
      });
    },

    async deleteCustomer(_id) {
      try {
        // eslint-disable-next-line
        let response = await this.axios.delete(`${process.env.VUE_APP_SERVER_URL}/customer/${_id}`);

        this.$modal.hide("dialog");
        this.handleSuccess(this.$t('delete_customer_modal.success'));

        this.loadCustomers();
      } catch (error) {
        this.$modal.hide("dialog");
        const safe4Text = this.safe4OffboardBlockedText(error);
        if (safe4Text) this.handleError(null, safe4Text, "top-right", 10000);
        else this.handleError(error);
      }
    },

    async exportXLS() {
      try {
        const response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/customer/`, {
          params: { page: 1, perPage: -1, ...this.filterParams() },
        });
        const allCustomers = response.data.data;
        let export_columns = [];
        allCustomers.forEach((customer) => {
          export_columns.push({
            [this.$t("admin_customer_export.customer_id")]: customer.customer_id,
            [this.$t("admin_customer_export.name")]: customer.name,
            [this.$t("admin_customer_export.org_number")]: customer.company_number,
            [this.$t("admin_customer_export.email")]: customer.email,
            [this.$t("admin_customer_export.phone")]: customer.phone,
            [this.$t("admin_customer_export.city_town")]: customer.city,
          });
        });
        this.excelExport(export_columns, this.$t("admin_customer_export.sheet_name"), this.$t("admin_customer_export.file_name"));
      } catch (error) {
        this.handleError(error);
      }
    },

    getSellerName(id) {
      let s = _.find(this.sellers, { _id: id });
      if (s) return s.name;
      else return "";
    },

    async show(customer) {
      try {
        const response = await this.axios.get(`${process.env.VUE_APP_SERVER_URL}/customer/${customer.customer_id}`);
        this.customer_info = _.cloneDeep(response.data.customer);
        this.$modal.show("update-customer");
      } catch (error) {
        this.handleError(error);
      }
    },

    showAddon(customer) {
      this.customer_info = _.cloneDeep(customer);
      this.$modal.show("update-addon");
    },
  },
  computed: {
    canFilterDutch() {
      return canFilterDutch(this.user);
    },
    columns() {
      return [
        { label: this.$t("customer_table.name"), field: "name", sortable: false },
        { label: this.$t("customer_table.seller"), field: "seller", sortable: false, formatFn: this.getSellerName, tdClass: "text-xxs" },
        { label: this.$t("customer_table.org_number"), field: "company_number", sortable: false },
        { label: this.$t("customer_table.email"), field: "email", sortable: false },
        { label: this.$t("customer_table.phone"), field: "phone", sortable: false },
        { label: this.$t("customer_table.city_town"), field: "city", sortable: false },
        { label: "", field: "agreement", sortable: false },
        { label: "", field: "tounit", sortable: false },
        { label: "", field: "toaddon", sortable: false },
        { label: "", field: "edit", sortable: false },
        { label: "", field: "delete", sortable: false },
      ];
    },
  },
  created() {
    const savedSearch = sessionStorage.getItem('customers_search');
    if (savedSearch) {
      this.serverParams.search = savedSearch;
    }
    this.dutchOnly = this.canFilterDutch && sessionStorage.getItem('customers_dutch') === '1';
    this.loadCustomers();
    this.getSellers();
    this.getAlarmCentrals();

    this.setPageTitle(this.$t("top_titles.customers_page"), "customers_admin");
  },
  mounted() {
    const savedSearch = sessionStorage.getItem('customers_search');
    if (savedSearch && this.$refs.customersTable) {
      this.$nextTick(() => {
        this.$refs.customersTable.globalSearchTerm = savedSearch;
      });
    }
  },
  watch: {
    "$i18n.locale"() {
      document.title = this.$t("page_titles.customer");
    },
  },
};
</script>
